/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import {
  Component, Inject, OnDestroy, OnInit,
} from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonSize } from 'src/aero/aero-button/aero-button.component';
import { BULB_BUSY_COVER_SERVICE } from '../../app/angularjs.providers';
import { ContentService } from '../../common/content.service';
import { TranslationService } from '../../common/translation.service';
import { UnitSimpleDTO } from './unit-simple-dto.model';
import KeyUpEvent = JQuery.KeyUpEvent;

/**
 * Angular component that will house all the logic and render states for the resume content selection modal.
 */
@Component({
  selector: 'bulb-resume-content-selection-modal',
  templateUrl: './content-selection-modal.component.html',
  standalone: false,
})
export class ResumeContentSelectionModalComponent implements OnInit, OnDestroy {
  /**
   * Title for the content selection modal. Could have three titles :
   * - Zero content title
   * - Content that is not published.
   * - Published content.
   */
  title : string;

  /**
   * Indication if the state has been fully loaded for the selection modal. i.e. units have been retrieved.
   */
  public viewStateFullyLoaded : boolean = false;

  /**
   * Boolean indicating if the user doesn't have any content -- published or not.
   */
  public zeroContentState : boolean = false;

  /**
   * Boolean indicating if the user HAS content, but NONE of it is published.
   */
  public hasPublishedContent : boolean = false;

  /**
   * Boolean indicating if the user has selected content or not.
   */
  public userHasSelectedContent : boolean = false;

  /**
   * Reference to the user's published content.
   */
  public userPublishedContent : Array< UnitSimpleDTO > = new Array< UnitSimpleDTO >();

  /**
   * Reference to the selected units from the user.
   * To be displayed as the selected Portfolio Content.
   */
  public selectedUnits : Array< UnitSimpleDTO > = new Array< UnitSimpleDTO >();

  /**
   * Button size of content selection modal close button
   */
  public buttonSize : ButtonSize = ButtonSize.LARGE;

  /**
   * Default Constructor.
   *
   * @param BulbAuthService
   * @param BulbBusyCoverService
   * @param contentService
   * @param dynamicDialogRef
   * @param dialogConfig
   * @param translationService
   */
  constructor(
    @Inject('BulbAuthService') private BulbAuthService,
    @Inject(BULB_BUSY_COVER_SERVICE) private BulbBusyCoverService,
    private contentService : ContentService,
    private dynamicDialogRef : DynamicDialogRef,
    private dialogConfig : DynamicDialogConfig,
    private translationService : TranslationService,
  ) {}

  /**
   * Closes the content selection modal when the user clicks the 'cancel' button or the bark arrow.
   */
  closeContentSelectionModal() : void {
    this.dynamicDialogRef.close();
  }

  /**
   * Closes the content selection modal when the user clicks the 'select' button, passing the selected unit.
   */
  closeContentSelectionModalOnSelect() : void {
    this.dynamicDialogRef.close(this.selectedUnits);
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a
   * directive or component.
   * Used to handle any additional initialization tasks.
   */
  ngOnInit() {
    // Make the REST call to the server, fetching the user's published content, pages and collections.
    this.BulbBusyCoverService.showBusyDuring(
      this.contentService.fetchPublishedContent(this.BulbAuthService.currentUser.authorUnitId)
        .then((units) => {
          this.registerDialogCloseOnEscapeKey();
          // Filter out all collections that don't have published pages or are empty:
          this.userPublishedContent = units
            .filter((unit) => ((unit.unitType === 'CompositeUnit') ? unit.children.length > 0 : true));
          // Update component properties necessary to display final modal view :
          this.updateModalState();
        }),
    );
  }

  /**
   * A callback method that performs custom clean-up, invoked immediately
   * after a directive, pipe, or service instance is destroyed.
   */
  ngOnDestroy() {
    this.unregisterDialogCloseOnEscapeKey();
  }

  /**
   * Helper method that updates the final view state of the content-selection-modal.
   */
  private updateModalState() : void {
    const titleValues : Array< string > = this.translationService.translateArrayInstant([
      'resume_content_selection_title_1',
      'resume_content_selection_title_2',
      'resume_content_selection_title_3',
    ]);

    // Update zero content boolean :
    this.zeroContentState = this.userPublishedContent.length === 0;
    // Update contentButNonePublished boolean :
    this.hasPublishedContent = this.userPublishedContent.length >= 1;

    // Update final title :
    // eslint-disable-next-line no-nested-ternary
    this.title = (this.zeroContentState) ? titleValues[0]
      : (!this.hasPublishedContent) ? titleValues[2] : titleValues[1];

    this.viewStateFullyLoaded = true;
  }

  /**
   * Handle the units the user has selected, send them off to the Server to save!
   *
   * @param event
   */
  handleSelectedUnits(event) : void {
    this.userHasSelectedContent = event.units.length > 0;
    this.selectedUnits = event.units;
  }

  /**
   * Helper method that registers the escape key to close the dialog.
   */
  private registerDialogCloseOnEscapeKey() : void {
    const $resumeContentSelectionDialog : JQuery = $(`.${this.dialogConfig.styleClass.split(' ')[0]}`);
    // Need to make the dialog focusable to enable key events:
    $resumeContentSelectionDialog.attr('tabindex', 0);
    $resumeContentSelectionDialog.trigger('focus');
    $resumeContentSelectionDialog.on('keyup', this.handleKeyUpEvent.bind(this));
  }

  /**
   * Helper method to unregister the 'keyup' event we register to the dialog window.
   */
  private unregisterDialogCloseOnEscapeKey() : void {
    const $resumeContentSelectionDialog : JQuery = $(`.${this.dialogConfig.styleClass.split(' ')[0]}`);
    $resumeContentSelectionDialog.attr('tabindex', '');
    $resumeContentSelectionDialog.off('keyup', this.handleKeyUpEvent);
  }

  /**
   * Helper method to handle the key up event on the content selection modal.
   *
   * @param event
   */
  private handleKeyUpEvent(event : KeyUpEvent) : void {
    if (event.key === 'Escape') {
      this.closeContentSelectionModal();
    }
  }
}
